import { Cart, CartItem, Order } from "./types"
import type {
  PageType,
  Cart as NostoCart,
  CartItem as NostoCartItem,
  WebsiteOrder as NostoOrder
} from "@nosto/client-lib"
import { nostojs } from "./api"

function toString<T>(value: T) {
  return typeof value === "number" ? String(value) : value
}

function lineItemToNosto(item: CartItem, currency: string): NostoCartItem {
  let itemPrice = +item.price
  if (item.discount_allocations && item.discount_allocations.length > 0) {
    const itemDiscount = item.discount_allocations
      .map(allocation => parseFloat(allocation.amount))
      .reduce((acc, d) => acc + d, 0)
    itemPrice = +(+itemPrice - itemDiscount / item.quantity).toFixed(2)
  }
  return {
    product_id: toString(item.product_id),
    sku_id: toString(item.variant_id),
    quantity: item.quantity,
    name: item.title,
    unit_price: itemPrice,
    price_currency_code: currency
  }
}

function cartToNosto(cart: Cart): NostoCart {
  const items = cart.items.filter(item => item.product_id).map(item => lineItemToNosto(item, cart.currency!))
  return { items }
}

function orderToNosto(order: Order): NostoOrder {
  const items = order.line_items.map(item => lineItemToNosto(item, order.presentment_currency || order.currency))
  return {
    info: {
      order_number: toString(order.order_id),
      email: order.email,
      first_name: order.billing_address?.first_name || "",
      last_name: order.billing_address?.last_name || "",
      type: "order"
    } as NostoOrder["info"],
    items: items
  } as NostoOrder
}

/**
 * Augments tagging via tagging providers based on ShopifyAnalytics object
 */
export function updateAnalyticsTagging() {
  if (window.ShopifyAnalytics?.meta?.page) {
    const { pageType, resourceId, resourceType } = window.ShopifyAnalytics.meta.page
    switch (pageType) {
      case "home":
        updatePageTypeTagging("front")
        break
      case "collection":
        updatePageTypeTagging("category")
        if (resourceType === "collection") {
          updateCategoryIdsTagging([String(resourceId)])
          // category data is taken from base tagging
        }
        break
      case "cart":
      case "product":
        updatePageTypeTagging(pageType)
        break
    }
  }
}

export function updatePageTypeTagging(pageType: PageType) {
  nostojs(api => api.internal.setTaggingProvider("pageType", pageType))
}

export function updateCategoryIdsTagging(categoryIds: string[]) {
  nostojs(api => api.internal.setTaggingProvider("categoryIds", categoryIds))
}

export function updateCartTagging(cart: Cart) {
  const nostoCart = cartToNosto(cart)
  nostojs(api => api.internal.setTaggingProvider("cart", nostoCart))
}

export function updateOrderTagging(order: Order) {
  const nostoOrder = orderToNosto(order)
  nostojs(api => api.internal.setTaggingProvider("order", nostoOrder))
}
